import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AlertDialogCancel,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const COUNTDOWN_SECONDS = 5;

interface DeleteAccountFormValues {
  acknowledge: boolean;
}

interface DeleteAccountFormProps {
  onConfirm: () => Promise<void>;
  isLoading: boolean;
  open: boolean;
}

export function DeleteAccountForm({
  onConfirm,
  isLoading,
  open,
}: DeleteAccountFormProps) {
  const { t } = useTranslation("settings");
  const form = useForm<DeleteAccountFormValues>({
    defaultValues: { acknowledge: false },
  });

  const acknowledge = form.watch("acknowledge");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!open) {
      form.reset({ acknowledge: false });
      setCountdown(COUNTDOWN_SECONDS);
    }
  }, [open, form]);

  useEffect(() => {
    if (!open) return;

    if (!acknowledge) {
      setCountdown(COUNTDOWN_SECONDS);
      return;
    }

    setCountdown(COUNTDOWN_SECONDS);

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [acknowledge, open]);

  const onSubmit = form.handleSubmit(async () => {
    if (!acknowledge || countdown > 0) {
      return;
    }

    try {
      await onConfirm();
    } catch (error) {
      // errors are surfaced via mutation onError handlers, and we keep the dialog open in the case of an error
      // you could close down the modal here, but that would typically signal to the user that the action was successful
    }
  });

  return (
    <Form {...form}>
      <div className="space-y-6">
        <FormField
          control={form.control}
          name="acknowledge"
          render={({ field }) => (
            <FormItem className="flex items-start gap-3">
              <FormControl>
                <Checkbox
                  id="delete-account-acknowledge"
                  checked={field.value}
                  onCheckedChange={(value) => field.onChange(Boolean(value))}
                />
              </FormControl>
              <div className="space-y-1">
                <FormLabel
                  htmlFor="delete-account-acknowledge"
                  className="text-sm font-semibold text-destructive"
                >
                  {t("deleteAccount.checkboxLabel")}
                </FormLabel>
                <FormDescription className="text-xs text-muted-foreground">
                  {t("deleteAccount.checkboxDescription")}
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        {acknowledge && countdown > 0 && (
          <p className="text-sm font-medium text-muted-foreground">
            {t("deleteAccount.countdown", { seconds: countdown })}
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel type="button">
            {t("deleteAccount.cancel")}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={!acknowledge || countdown > 0 || isLoading}
            isLoading={isLoading}
            loadText={t("deleteAccount.deleting")}
            onClick={onSubmit}
          >
            {t("deleteAccount.confirmButton")}
          </Button>
        </AlertDialogFooter>
      </div>
    </Form>
  );
}
